import {defineStore} from "pinia";
import {useUserStore} from "@/stores/user.js";
import api from "@/api.js";
import router from "@/router/index.js";
import {useConfigStore} from "@/stores/config.js";
import {groupMessagesByDay} from '@/utils/groupMessagesByDay'
import packageJson from "../../package.json";
import {getAuthenticatedRequest} from "@/stores/AuthenticatedRequest.js";
import {useApi} from "@/services/useApi.js";
import {client} from '@/stores/client.js'

export const useChatStore = defineStore('chat', {
  state: () => ({
    activeChat: false,
    activeFilePreview: false,
    activeMobileSidebar: false,
    selectedFile: {},
    allContacts: [],
    selectedChat: {},
    chatData: {},
    searchQuery: '',
    filters: {
      stores: [],
      services: [],
      games: [],
      gameServices: {},
      pendingReply: false
    },
    allStores: [
      {
        value: 'DealHub USD',
        label: 'DealHub USD',
      },
      {
        value: 'Incepto USD',
        label: 'Incepto USD',
      }
    ],
    allServices: [
      {
        value: 'Account',
        label: 'Account',
      },
      {
        value: 'Boost',
        label: 'Boost',
      },
      {
        value: 'Currency',
        label: 'Currency',
      },
      {
        value: 'Item',
        label: 'Item',
      },
      {
        value: 'Gift Card',
        label: 'Gift Card',
      }
    ],
    isGettingNewMessage: false,
    isGettingNewContacts: false,
    filterDrawer: false,
    loadingMessages: false,
    searchContactError: '',
    pendingMessages: [],
    pendingMessagesByChannel: {}
  }),
  getters: {
    get_searched_chats(state) {
      const query = state.searchQuery.trim().toLowerCase();
      return state.allContacts.filter((chat) => {
        return chat.contact.toLowerCase().includes(query) ||
          chat.orderType?.toLowerCase().includes(query) ||
          chat.channel?.toLowerCase().includes(query) ||
          chat.orderGame?.toLowerCase().includes(query);
      });
    },
    get_all_contacts(state) {
      const query = state.searchQuery.trim().toLowerCase();
      const now = Date.now();

      // 🧩 Step 1: Determine the urgency of each contact
      let processedContacts = state.allContacts.map((msg) => {
        const isUrgentData = this.checkUrgency(msg)
        return {...msg, ...isUrgentData};
      });

      // 🧩 Step 2: Sort (Emergencies at the top)
      processedContacts.sort((a, b) => {
        if (a.isUrgent && !b.isUrgent) return -1;
        if (!a.isUrgent && b.isUrgent) return 1;
        if (a.isUrgent && b.isUrgent) return b.urgencyLevel - a.urgencyLevel; // High priority contacts are ranked higher.
        return b.lastMessageTime - a.lastMessageTime; // Regular contacts are arranged in chronological order.
      });

      // 🧩 Step 3: Filtering
      return processedContacts.filter((msg) => {

        // Keep contacts without a game or service visible regardless of active filters.
        if (!msg.orderGame || !msg.orderType) {
          return true;
        }

        const matchStore =
          !state.filters.stores.length ||
          state.filters.stores.length === state.allStores.length ||
          state.filters.stores.some((store) => store.value === msg.store);

        const hasGameServiceFilter = Object.keys(state.filters.gameServices || {}).length > 0;
        const matchGameService =
          !hasGameServiceFilter ||
          state.filters.gameServices[msg.orderGame]?.includes(msg.orderType);

        const isUnread =
          msg.unreadMessages > 0 ||
          (msg.unreadMessages <= 0 &&
            !(msg.lastMessageFromID === "1001234905") &&
            !(msg.lastMessageFromID === "1000670527"));

        const matchReadStatus =
          !state.filters.pendingReply ||
          (state.filters.pendingReply && isUnread);

        const matchQuery =
          msg.contact?.toLowerCase().includes(query) ||
          msg.orderType?.toLowerCase().includes(query) ||
          msg.channel?.toLowerCase().includes(query) ||
          msg.orderGame?.toLowerCase().includes(query);

        // ⏰ calculate customizedTime (passed 4 hours)
        const customizedTimePassed = now - (msg.customizedTime || 0);
        const FOUR_HOURS = 4 * 60 * 60 * 1000;
        const fourHoursPassed = customizedTimePassed > FOUR_HOURS;

        // ✅ Always show if four hours passed and it is urgent
        if (msg.isUrgent && fourHoursPassed) {
          return true;
        }

        // ✅ Mode 1: If the user is searching
        // Only contacts that match the search are displayed.
        if (query) {
          return matchQuery && matchReadStatus;
        }

        // ✅ Case 2: If search is empty, emergencies should always be seen
        if (msg.isUrgent && !fourHoursPassed) {
          return matchStore && matchGameService && matchReadStatus && matchQuery;
        }

        // ✅ Mode 3: Otherwise, apply regular filters.
        return matchStore && matchGameService && matchReadStatus && matchQuery;
      });
    },
    get_reversed_messages(state) {
      return state.chatData?.messages ? [...state.chatData.messages].reverse() : []
    },
    get_grouped_messages_by_day(state) {
      return state.chatData?.messages ? groupMessagesByDay(state.chatData.messages) : []
    },
    get_all_messages(state) {

    },
    get_all_games() {
      const configStore = useConfigStore();
      return configStore.getAllGames.map(game => ({
        value: game.game,
        label: game.game
      }));
    },
    get_is_filter_active(state) {
      const oneStoreSelected = state.filters.stores.length === 1;
      const hasGameServiceFilter = Object.keys(state.filters.gameServices || {}).length > 0;
      return oneStoreSelected || hasGameServiceFilter;
    },
    get_is_pending_reply_active(state) {
      return state.filters.pendingReply;
    },
    get_images_in_chat(state) {
      return state.chatData?.messages ?
        state.chatData.messages.filter(item => item.type === "file" && item.file?.type.startsWith("image"))
          .map(item => item.file.url) : [];
    },
    highest_last_update(state) {
      return state.allContacts.length > 0
        ? Math.max(...state.allContacts.map(chat => chat.lastUpdate))
        : null; // Return null if there are no chats
    },
    get_pendingMessagesByChannel(state) {
      const channel = state.chatData?.channel;
      if (!channel) return [];
      return state.pendingMessagesByChannel[channel] || [];
    },
  },
  actions: {
    addPendingMessage1(msg) {
      const channel = this.chatData.channel;
      if (!this.pendingMessagesByChannel[channel]) {
        this.pendingMessagesByChannel[channel] = [];
      }
      this.pendingMessagesByChannel[channel].unshift(msg);
    },
    removePendingMessage(id) {
      const channel = this.chatData.channel;
      const index = this.pendingMessagesByChannel[channel].findIndex(m => m.id === id);
      if (index > -1) this.pendingMessagesByChannel[channel].splice(index, 1);
    },
    updateChats(newChats) {

      if (newChats.length > 0) {
        if (newChats.find(item => item.channel === this.chatData.channel)) {
          const updatedChatData = newChats.find(item => item.channel === this.chatData.channel)
          //console.log(newChats.find(item => item.channel === this.chatData.channel))
          //console.log(this.chatData)
          //console.log("-----------------------------------")
          if (updatedChatData) {
            this.chatData.isTyping = updatedChatData.isTyping
            this.chatData.lastRead = updatedChatData.lastRead
            const isUrgentData = this.checkUrgency(updatedChatData)
            this.chatData['isUrgent'] = isUrgentData.isUrgent
            this.chatData['urgencyLevel'] = isUrgentData.urgencyLevel
            this.chatData['urgencyEmoji'] = isUrgentData.urgencyEmoji
          }
        }
      }

      const chatMap = new Map(this.allContacts.map(chat => [chat.channel, chat]));

      // Replace existing chats or add new ones
      newChats.forEach(chat => {
        chatMap.set(chat.channel, chat);
      });

      // Convert back to array and sort by lastMessageTime
      this.allContacts = Array.from(chatMap.values()).sort((a, b) => b.lastMessageTime - a.lastMessageTime);

      //console.log(this.allContacts.find(item => item.channel === this.chatData.channel))
      /*const found = this.allContacts.find(item => item.channel === this.chatData.channel)
      if (found) {
        console.log('here')
        this.chatData = found
      }*/

    },
    toggleFilterDrawer() {
      this.filterDrawer = !this.filterDrawer;
    },
    toggleMobileSidebar() {
      this.activeMobileSidebar = !this.activeMobileSidebar;
    },
    openChat(data) {
      this.activeChat = true;
      this.selectedChat = data;
      this.activeMobileSidebar = false
    },
    async action_get_all_contacts() {
      const userStore = useUserStore()
      const request = {
        "request": 1009,
        "subRequest": 10135,
        "account": {
          "id": userStore.getUserId,
          "credential": userStore.getCredential
        },
        "client": client
      };
      //console.log('action_get_all_contacts request', request)
      this.isGettingNewContacts = true;
      return await api.post('', request)
        .then((res) => {
          if (res.status === 200) {
            return res.data
          } else {
            console.log('response is ' + res.status)
          }
        }).then((data) => {
          //console.log(data)
          if (data.response === 100) {
            //console.log('action_get_all_contacts success', data)
            this.set_all_contacts(data.chats)
            this.isGettingNewContacts = false;
            return true
          } else if (data.response === -100002) {
            userStore.resetUser('your credential is expired')
            router.push({name: 'Login'})
          } else {
            this.errorMessage = data.message
            return false
          }
        })
    },
    async action_get_new_contacts() {
      const userStore = useUserStore()
      const request = {
        "request": 1009,
        "subRequest": 10135,
        "account": {
          "id": userStore.getUserId,
          "credential": userStore.getCredential
        },
        "client": client,
        "chat": {
          "lastUpdate": this.highest_last_update
        }
      };
      //console.log('action_get_new_contacts request', request)
      this.isGettingNewContacts = true;
      return await api.post("", request)
        .then((res) => (res.status === 200 ? res.data : console.log("Response status:", res.status)))
        .then((data) => {
          if (data?.response === 100) {
            this.updateChats(data.chats)
            //console.log('action_get_new_contacts success', data.chats)
          } else if (data?.response === -100002) {
            userStore.resetUser("Your credential is expired");
            router.push({name: "Login"});
          } else {
            this.errorMessage = data?.message || "Unknown error";
          }
        })
        .catch((err) => {
          console.error("API error:", err);
        })
        .finally(() => {
          this.isGettingNewContacts = false;
        });
    },
    async action_get_chat() {
      const userStore = useUserStore()
      //console.log(this.selectedChat.channel)
      const request = {
        "request": 1009,
        "subRequest": 10152,
        "account": {
          "id": userStore.getUserId,
          "credential": userStore.getCredential
        },
        "client": client,
        "chat": {
          "channel": this.selectedChat.channel,
          "store": this.selectedChat.store,
          "storeID": this.selectedChat.storeID
        }
      };
      this.loadingMessages = true
      this.set_chat_data({})
      return await api.post('', request)
        .then((res) => {
          if (res.status === 200) {
            return res.data
          } else {
            console.log('response is ' + res.status)
          }
        }).then((data) => {
          //console.log(data)
          if (data.response === 100) {
            //console.log('action_get_chat success', data)
            this.set_chat_data(data.chat)

            const channel = this.chatData.channel

            if (this.pendingMessagesByChannel[channel]?.length) {
              this.pendingMessagesByChannel[channel] =
                this.pendingMessagesByChannel[channel].filter(pending => {

                  const exists = this.chatData.messages.some(msg => {
                    const diff = Math.abs(
                      new Date(msg.time).getTime() -
                      pending.time.getTime()
                    )

                    return (
                      msg.type === pending.type &&
                      msg.message === pending.message &&
                      diff < 60000
                    )
                  })

                  return !exists
                })

              if (!this.pendingMessagesByChannel[channel].length) {
                delete this.pendingMessagesByChannel[channel]
              }
            }


            //console.log('old messages', this.chatData.messages)
            this.loadingMessages = false
            return true
          } else if (data.response === -100002) {
            userStore.resetUser('your credential is expired')
            router.push({name: 'Login'})
          } else {
            this.errorMessage = data.message
            return false
          }
        })
    },
    async action_get_chat_after_messageID() {
      const userStore = useUserStore();
      const request = {
        request: 1009,
        subRequest: 10153,
        account: {
          id: userStore.getUserId,
          credential: userStore.getCredential,
        },
        client,
        chat: {
          channel: this.selectedChat.channel,
          message: {
            id: this.chatData.messages[0]?.id,
            lastUpdate: this.highest_last_update,
          },
        },
      };

      this.isGettingNewMessage = true;
      return await api.post("", request)
        .then((res) => (res.status === 200 ? res.data : console.log("Response status:", res.status)))
        .then((data) => {
          if (data?.response === 100) {
            /*if (data.chat.messages.length > 0) {
              console.log('new messages', data.chat.messages)
            }*/
            this.place_new_messages(data.chat.messages);
          } else if (data?.response === -100002) {
            userStore.resetUser("Your credential is expired");
            router.push({name: "Login"});
          } else {
            this.errorMessage = data?.message || "Unknown error";
          }
        })
        .catch((err) => {
          console.error("API error:", err);
        })
        .finally(() => {
          this.isGettingNewMessage = false;
        });
    },
    async action_send_message(payload) {
      const userStore = useUserStore()
      const request = {
        "request": 1009,
        "subRequest": 10154,
        "account": {
          "id": userStore.getUserId,
          "credential": userStore.getCredential
        },
        "client": client,
        "chat": {
          "store": this.selectedChat.store,
          "channel": this.selectedChat.channel,
          "message": {
            "message": payload.message
          }
        }
      };
      return await api.post('', request)
        .then((res) => {
          if (res.status === 200) {
            return res.data
          } else {
            console.log('response is ' + res.status)
          }
        }).then((data) => {
          //console.log(data)
          if (data.response === 100) {
            /*if (this.pendingMessagesByChannel[data.chat.channel]) {
              this.pendingMessagesByChannel[data.chat.channel] = this.pendingMessagesByChannel[data.chat.channel].filter(msg => msg.message !== data.chat.message.message);

              if (this.pendingMessagesByChannel[data.chat.channel].length === 0) delete this.pendingMessagesByChannel[data.chat.channel];
            }*/
            console.log('action_send_message success', data)
            //return true
          } else if (data.response === -100002) {
            userStore.resetUser('your credential is expired')
            router.push({name: 'Login'})
          } else {
            this.errorMessage = data.message
            return false
          }
        })
    },
    async action_send_file(payload) {
      const userStore = useUserStore()
      const request = {
        "request": 1009,
        "subRequest": 10155,
        "account": {
          "id": userStore.getUserId,
          "credential": userStore.getCredential
        },
        "client": client,
        "chat": {
          "store": payload.store,
          "channel": payload.channel,
          "message": {
            "fileName": payload.fileName
          }
        }
      };
      return await api.post('', request)
        .then((res) => {
          if (res.status === 200) {
            return res.data
          } else {
            console.log('response is ' + res.status)
          }
        }).then((data) => {
          //console.log(data)
          if (data.response === 100) {
            if (this.pendingMessagesByChannel[data.chat.channel]) {
              this.pendingMessagesByChannel[data.chat.channel] = this.pendingMessagesByChannel[data.chat.channel].filter(msg => msg.file.name !== data.chat.message.fileName);

              if (this.pendingMessagesByChannel[data.chat.channel].length === 0) delete this.pendingMessagesByChannel[data.chat.channel];
            }
            console.log('action_send_file success', data)
            //return true
          } else if (data.response === -100002) {
            userStore.resetUser('your credential is expired')
            router.push({name: 'Login'})
          } else {
            this.errorMessage = data.message
            return false
          }
        })
    },
    async action_customizeChannel(payload) {
      const request = {
        ...getAuthenticatedRequest(),
        "subRequest": 10165,
        "chat": {
          "channel": this.selectedChat.channel,
          "orderType": payload.orderType,
          "orderGame": payload.orderGame
        }
      };
      console.log('action_customizeChannel: ', request);
      return await useApi(
        request,
        (response) => {
          console.log('action_customizeChannel Success: ', response);
          return true;
        },
        (errorMessage) => {
          console.log('Handle Credential Expired: ', errorMessage);
          return errorMessage;
          // userStore.resetUser(errorMessage);
        },
        (errorMessage) => {
          console.log('Handle Failure: ', errorMessage);
          return errorMessage;
          // userStore.setErrorMessage(errorMessage);
        }
      );
    },
    async action_searchContact(payload) {
      const request = {
        ...getAuthenticatedRequest(),
        "subRequest": 2067,
        "chat": {
          "contact": payload.keyword
        }
      };
      //console.log('action_searchContact: ', request);
      return await useApi(
        request,
        (response) => {
          //console.log('action_searchContact Success: ', response);
          return {
            result: true
          };
        },
        (errorMessage) => {
          console.log('Handle Credential Expired: ', errorMessage);
          return errorMessage;
          // userStore.resetUser(errorMessage);
        },
        (errorMessage) => {
          console.log('Handle Failure: ', errorMessage);
          return errorMessage;
          // userStore.setErrorMessage(errorMessage);
        }
      );
    },
    set_all_contacts(payload) {
      this.allContacts = payload
    },
    set_store_filter(payload) {
      this.filters.stores = payload
    },
    set_service_filter(payload) {
      this.filters.services = payload
    },
    set_game_filter(payload) {
      this.filters.games = payload
    },
    reset_filters() {
      this.set_store_filter([])
      this.set_service_filter([])
      this.set_game_filter([])
      this.filters.gameServices = {}
    },
    checkUrgency(payload) {
      const now = Date.now();

      // Emergency time calculation (in milliseconds)
      const FIVE_MIN = 5 * 60 * 1000;
      const TEN_MIN = 10 * 60 * 1000;
      const FIFTEEN_MIN = 15 * 60 * 1000;
      const THREE_DAYS = 3 * 24 * 60 * 60 * 1000;

      // 1️⃣ Check unanswered messages
      const hasUnanswered = payload.unreadMessages > 0 || (payload.lastMessageFromID &&
        !(payload.lastMessageFromID === "1001234905") && !(payload.lastMessageFromID === "1000670527"));

      // 2️⃣ Check time since last message
      const timePassed = now - payload.lastMessageTime;

      // Emergency flag
      let isUrgent = false;
      let urgencyLevel = null;
      let urgencyEmoji = null;

      // 🚨 Check only if it's been less than 3 days.
      if (hasUnanswered && timePassed < THREE_DAYS) {
        if (timePassed >= FIFTEEN_MIN) {
          isUrgent = true;
          urgencyLevel = 3;
          urgencyEmoji = "🤬";
        } else if (timePassed >= TEN_MIN) {
          isUrgent = true;
          urgencyLevel = 2;
          urgencyEmoji = "😤";
        } else if (timePassed >= FIVE_MIN) {
          isUrgent = true;
          urgencyLevel = 1;
          urgencyEmoji = "😒";
        }
      }

      return {
        isUrgent,
        urgencyLevel,
        urgencyEmoji,
      }
    },
    set_chat_data(payload) {
      this.chatData = payload
      const isUrgentData = this.checkUrgency(payload)
      this.chatData['isUrgent'] = isUrgentData.isUrgent
      this.chatData['urgencyLevel'] = isUrgentData.urgencyLevel
      this.chatData['urgencyEmoji'] = isUrgentData.urgencyEmoji
    },
    place_new_messages(payload) {
      if (this.selectedChat.channel !== this.chatData.channel) {
        return;
      }

      const oldMap = new Map(this.chatData.messages.map((msg) => [msg.id, msg]));

      payload.forEach((newMsg) => {
        if (oldMap.has(newMsg.id)) {
          //console.log('has it')
          // اگر پیام با این ID قبلاً وجود دارد، جایگزینش کن
          const index = this.chatData.messages.findIndex((msg) => msg.id === newMsg.id);
          if (index !== -1) {
            this.chatData.messages[index] = newMsg;
          }
        } else {
          //console.log('does not have it')
          // اگر پیام جدید است، به ابتدای لیست اضافه کن
          this.chatData.messages.unshift(newMsg);
        }
      });
    },
    /*place_new_messages(payload) {
      if (this.selectedChat.channel !== this.chatData.channel) {
        // لاگ کردن اطلاعات اولیه برای دیباگ
        console.log('place_new_messages called', {
          selectedChatChannel: this.selectedChat.channel,
          chatDataChannel: this.chatData.channel,
          payload: payload,
          currentMessages: [...this.chatData.messages], // کپی از پیام‌های فعلی
        });
      }

      // بررسی اینکه آیا کانال انتخاب‌شده با کانال داده‌های چت مطابقت دارد
      if (this.selectedChat.channel !== this.chatData.channel) {
        console.log('Channel mismatch! Ignoring new messages.', {
          selectedChatChannel: this.selectedChat.channel,
          chatDataChannel: this.chatData.channel,
          payload: payload,
        });
        //return; // از افزودن پیام‌ها جلوگیری می‌کند
      }

      // ایجاد نقشه از پیام‌های موجود برای بررسی پیام‌های تکراری
      const oldMap = new Map(this.chatData.messages.map((msg) => [msg.id, msg]));

      // افزودن یا به‌روزرسانی پیام‌های جدید
      payload.forEach((newMsg) => {
        if (oldMap.has(newMsg.id)) {
          console.log('Updating existing message', newMsg);
          const index = this.chatData.messages.findIndex((msg) => msg.id === newMsg.id);
          if (index !== -1) {
            this.chatData.messages[index] = newMsg;
          }
        } else {
          console.log('Adding new message', newMsg);
          this.chatData.messages.unshift(newMsg);
        }
      });

      if (this.selectedChat.channel !== this.chatData.channel) {
        // لاگ کردن پیام‌های نهایی برای بررسی
        console.log('Messages after update', {
          updatedMessages: [...this.chatData.messages],
          selectedChatChannel: this.selectedChat.channel,
          chatDataChannel: this.chatData.channel,
        });
        debugger
      }
    },*/
  },
  persist: {
    paths: ['filters.stores', 'filters.gameServices'],
  },
})
